import type { VisualRandom } from '../core/VisualRandom.js';
import type { VisualSignalFrame } from '../signals/VisualSignal.js';

/**
 * Where the next thing goes.
 *
 * Everything used to appear at the middle of the field, which is why a busy
 * passage read as one pile rather than as a composition. Scattering it with a
 * random number would replace one problem with a worse one: uniform noise over a
 * square puts marks in the corners, drops them off the edge, and — because
 * nothing remembers anything — cannot tell a run that is building up from one
 * that is falling apart.
 *
 * So this is **random with a composition**, and it is made of three parts:
 *
 *   - **Modes**, which are intentions rather than distributions. `RANDOM_RING`
 *     is not "random"; it is "somewhere around a circle", which is a shape a
 *     viewer can see even when the individual position is unpredictable.
 *   - **Safety**, applied to every mode's answer without exception. A position
 *     is clamped into the field, kept off the edge, and rejected if it lands on
 *     top of something recent — with a bounded number of retries, because a
 *     rejection loop that can run forever is not a safety feature.
 *   - **History**, which is what makes a run legible. `FOLLOW_PREVIOUS` grows a
 *     cluster, `AVOID_PREVIOUS` scatters away from one, and `OPPOSITE_SIDE`
 *     throws the next mark across the field. Mapped to combo, combo break and
 *     off-beat presses, those three turn the shape of the composition into a
 *     record of how the run went.
 *
 * Coordinates are field units throughout: 1 is the playfield radius. See
 * `WaveEvent`.
 *
 * Every number comes from the `VisualRandom` it is handed. There is no
 * `Math.random()` here, and there must not be: the whole point is that the same
 * replay, recipe and seed produce the same composition.
 */

export const SPATIAL_MODES = [
  'CENTER',
  'RANDOM',
  'RANDOM_RING',
  'EDGE',
  'ORBIT',
  'FOLLOW_PREVIOUS',
  'AVOID_PREVIOUS',
  'OPPOSITE_SIDE',
  'AUDIO_REACTIVE',
] as const;

export type SpatialMode = (typeof SPATIAL_MODES)[number];

export function isSpatialMode(value: string): value is SpatialMode {
  return (SPATIAL_MODES as readonly string[]).includes(value);
}

export interface SpatialPlacement {
  readonly x: number;
  readonly y: number;
  /** Radians, outward from the composition's centre unless a mode says otherwise. */
  readonly direction: number;
  /** Which rule produced this, for the spawn log. */
  readonly mode: SpatialMode;
  /** How many candidates were rejected before this one was accepted. */
  readonly rejected: number;
}

export interface SpatialLimits {
  /** Nothing is placed further from the centre than this, in field units. */
  readonly maxRadius: number;
  /**
   * How far in from `maxRadius` the band of usable space starts to be kept
   * clear, so that `EDGE` sits *near* the edge rather than on it.
   */
  readonly edgeMargin: number;
  /** Candidates closer than this to a recent placement are rejected. */
  readonly minSpacing: number;
  /** How many rejections to accept before taking the best candidate anyway. */
  readonly maxAttempts: number;
  /** How many past placements to remember. */
  readonly historyLength: number;
}

export const SPATIAL_DEFAULTS: SpatialLimits = {
  // Inside the field rather than at its rim: a mark centred on the rim is half
  // outside the frame at any aspect ratio narrower than square.
  maxRadius: 0.82,
  edgeMargin: 0.16,
  // About a fifth of the field. Two marks closer than this overlap enough to
  // read as one, which is what made dense passages look like a single blob.
  minSpacing: 0.2,
  maxAttempts: 6,
  historyLength: 8,
};

export class SpatialPlanner {
  private readonly limits: SpatialLimits;
  private readonly history: { x: number; y: number }[] = [];
  private orbitPhase = 0;
  private rejectedTotal = 0;
  private placed = 0;

  constructor(limits: Partial<SpatialLimits> = {}) {
    this.limits = { ...SPATIAL_DEFAULTS, ...limits };
  }

  reset(): void {
    this.history.length = 0;
    this.orbitPhase = 0;
    this.rejectedTotal = 0;
    this.placed = 0;
  }

  /**
   * The composition moves on.
   *
   * Half the history is dropped and the orbit is advanced, so the next few
   * events are free to occupy space the last few were keeping clear. Mapped
   * to the bar, this is what stops a long passage settling into one
   * arrangement it can never leave — without wiping the memory outright,
   * which would let the new marks land straight on top of the old ones.
   */
  shift(random: VisualRandom): void {
    const keep = Math.floor(this.history.length / 2);
    this.history.splice(0, this.history.length - keep);
    this.orbitPhase += random.range(0.4, 1.6);
  }

  /** The most recent accepted placement, or the centre if there is none. */
  get last(): { readonly x: number; readonly y: number } {
    return this.history[this.history.length - 1] ?? { x: 0, y: 0 };
  }

  /** Where the composition currently sits, as the mean of what is remembered. */
  get centroid(): { readonly x: number; readonly y: number } {
    if (this.history.length === 0) return { x: 0, y: 0 };
    let x = 0;
    let y = 0;
    for (const entry of this.history) {
      x += entry.x;
      y += entry.y;
    }
    return { x: x / this.history.length, y: y / this.history.length };
  }

  /**
   * Picks a position.
   *
   * `advance` decides whether the result is remembered. A caller that is only
   * previewing a layout — the HUD, a test — passes false, so looking does not
   * change what happens next.
   */
  place(options: {
    readonly mode: SpatialMode;
    readonly random: VisualRandom;
    readonly signals: VisualSignalFrame;
    /** Seconds. Only `ORBIT` uses it, to keep turning between events. */
    readonly deltaTime?: number;
    readonly advance?: boolean;
  }): SpatialPlacement {
    const { mode, random, signals } = options;
    this.orbitPhase += (options.deltaTime ?? 0) * 0.6;

    let best: { x: number; y: number } | null = null;
    let bestClearance = -1;
    let rejected = 0;

    for (let attempt = 0; attempt < this.limits.maxAttempts; attempt += 1) {
      const candidate = this.clampIntoField(this.propose(mode, random, signals, attempt));
      const clearance = this.clearanceOf(candidate);
      if (clearance > bestClearance) {
        bestClearance = clearance;
        best = candidate;
      }
      // `FOLLOW_PREVIOUS` is *supposed* to land near the last one, so the
      // spacing rule would reject exactly the thing it was asked for.
      if (mode === 'FOLLOW_PREVIOUS' || clearance >= this.limits.minSpacing) {
        best = candidate;
        break;
      }
      rejected += 1;
    }

    const point = best ?? { x: 0, y: 0 };
    const placement: SpatialPlacement = {
      x: point.x,
      y: point.y,
      direction: this.directionFor(mode, point, random),
      mode,
      rejected,
    };

    if (options.advance !== false) {
      this.rejectedTotal += rejected;
      this.placed += 1;
      this.history.push({ x: point.x, y: point.y });
      while (this.history.length > this.limits.historyLength) this.history.shift();
    }
    return placement;
  }

  /**
   * One mode's raw answer, before safety.
   *
   * `attempt` is passed in so a mode can move on being rejected rather than
   * proposing the same point six times — without it, `FOLLOW_PREVIOUS` in a
   * crowded corner would burn every attempt on the identical candidate.
   */
  private propose(
    mode: SpatialMode,
    random: VisualRandom,
    signals: VisualSignalFrame,
    attempt: number,
  ): { x: number; y: number } {
    const reach = this.limits.maxRadius;
    const spread = 1 + attempt * 0.35;

    switch (mode) {
      case 'CENTER': {
        // Not literally the centre: a stack of marks at exactly one point is a
        // single mark. A small jitter keeps it a cluster.
        const jitter = 0.06 * spread;
        return { x: random.signed() * jitter, y: random.signed() * jitter };
      }

      case 'RANDOM': {
        // Sampled on the disc, not the square. Uniform x and y put a corner of
        // the frame — where nothing else is — at 27% of the draws.
        const angle = random.range(0, Math.PI * 2);
        const radius = Math.sqrt(random.next()) * reach;
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }

      case 'RANDOM_RING': {
        // A band, not a line. The angle is free and the radius is nearly fixed,
        // which is what makes the ring visible while each position is not.
        const angle = random.range(0, Math.PI * 2);
        const radius = reach * random.range(0.55, 0.78);
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }

      case 'EDGE': {
        const angle = random.range(0, Math.PI * 2);
        const radius = reach - this.limits.edgeMargin * random.range(0, 0.6);
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }

      case 'ORBIT': {
        // Successive events walk around a circle instead of jumping, so a run of
        // them draws an arc. The step is deliberately not a divisor of a full
        // turn, or every bar would land on the same few points.
        const angle = this.orbitPhase + this.placed * 0.83 + random.signed() * 0.12;
        const radius = reach * random.range(0.5, 0.72);
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }

      case 'FOLLOW_PREVIOUS': {
        const from = this.last;
        const step = 0.14 + random.next() * 0.16 * spread;
        const angle = random.range(0, Math.PI * 2);
        return { x: from.x + Math.cos(angle) * step, y: from.y + Math.sin(angle) * step };
      }

      case 'AVOID_PREVIOUS': {
        // Pushed away from everything remembered, not just the last one, so a
        // cluster repels as a cluster.
        const angle = random.range(0, Math.PI * 2);
        const radius = Math.sqrt(random.next()) * reach;
        const candidate = { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
        const away = this.awayFromHistory(candidate);
        const push = 0.22 * spread;
        return { x: candidate.x + away.x * push, y: candidate.y + away.y * push };
      }

      case 'OPPOSITE_SIDE': {
        // Straight across the composition's centre of mass. This is the combo
        // break: whatever was building, the next mark is on the other side.
        const from = this.last;
        const length = Math.hypot(from.x, from.y);
        const radius = reach * random.range(0.45, 0.8);
        if (length < 1e-4) {
          const angle = random.range(0, Math.PI * 2);
          return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
        }
        const angle = Math.atan2(-from.y, -from.x) + random.signed() * 0.4;
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }

      default: {
        // AUDIO_REACTIVE. The bands are read as a place rather than as a size:
        // bass sits low and central because that is where weight belongs, treble
        // rides high and wide, and the amplitude decides how far out it all is.
        const bass = clamp01(signals.BASS);
        const treble = clamp01(signals.TREBLE);
        const radius = reach * (0.2 + clamp01(signals.AMPLITUDE) * 0.7);
        const angle =
          Math.PI * (0.5 + (treble - bass) * 0.8) + random.signed() * (0.5 + treble * 0.9);
        const height = (treble - bass) * 0.4;
        return {
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius * 0.75 + height * reach,
        };
      }
    }
  }

  /** Which way the disturbance travels from where it landed. */
  private directionFor(
    mode: SpatialMode,
    point: { x: number; y: number },
    random: VisualRandom,
  ): number {
    const outward = Math.atan2(point.y, point.x);
    switch (mode) {
      case 'EDGE':
        // Inward. Something arriving at the rim is coming into the picture.
        return outward + Math.PI + random.signed() * 0.3;
      case 'ORBIT':
        // Tangential, so an orbiting run reads as travelling around rather than
        // as a row of separate arrivals.
        return outward + Math.PI / 2;
      case 'FOLLOW_PREVIOUS': {
        const from = this.last;
        const dx = point.x - from.x;
        const dy = point.y - from.y;
        // Along the trail it is extending.
        if (Math.hypot(dx, dy) > 1e-4) return Math.atan2(dy, dx);
        return random.range(0, Math.PI * 2);
      }
      case 'CENTER':
        return random.range(0, Math.PI * 2);
      default:
        return outward + random.signed() * 0.5;
    }
  }

  /** The unit vector pointing away from the remembered placements. */
  private awayFromHistory(from: { x: number; y: number }): { x: number; y: number } {
    let x = 0;
    let y = 0;
    for (const entry of this.history) {
      const dx = from.x - entry.x;
      const dy = from.y - entry.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 1e-4) continue;
      // Nearer neighbours push harder.
      const weight = 1 / (distance * distance);
      x += (dx / distance) * weight;
      y += (dy / distance) * weight;
    }
    const length = Math.hypot(x, y);
    if (length < 1e-6) return { x: 0, y: 0 };
    return { x: x / length, y: y / length };
  }

  /** Distance to the nearest thing remembered. Large when the field is empty. */
  private clearanceOf(point: { x: number; y: number }): number {
    let nearest = Number.POSITIVE_INFINITY;
    for (const entry of this.history) {
      nearest = Math.min(nearest, Math.hypot(point.x - entry.x, point.y - entry.y));
    }
    return Number.isFinite(nearest) ? nearest : this.limits.minSpacing * 4;
  }

  /**
   * Pulls a candidate back inside the usable field.
   *
   * Applied to every mode's answer without exception, which is why the modes
   * above are free to add offsets that would otherwise walk off the frame —
   * `FOLLOW_PREVIOUS` and `AVOID_PREVIOUS` both do, by construction.
   */
  private clampIntoField(point: { x: number; y: number }): { x: number; y: number } {
    const limit = this.limits.maxRadius;
    const radius = Math.hypot(point.x, point.y);
    if (!Number.isFinite(radius)) return { x: 0, y: 0 };
    if (radius <= limit) return point;
    // Reflected back in rather than clamped onto the boundary: clamping puts a
    // whole run of rejected candidates on the same circle, which is a ring
    // nobody asked for and the most obvious artefact of naive placement.
    const overshoot = Math.min(radius - limit, limit);
    const scale = (limit - overshoot * 0.5) / radius;
    return { x: point.x * scale, y: point.y * scale };
  }

  diagnostics(): {
    readonly placed: number;
    readonly rejected: number;
    readonly history: number;
    readonly centroid: { readonly x: number; readonly y: number };
    readonly limits: SpatialLimits;
  } {
    return {
      placed: this.placed,
      rejected: this.rejectedTotal,
      history: this.history.length,
      centroid: this.centroid,
      limits: this.limits,
    };
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
